import type {
  LiveOpenShipment,
  LivePackingSlip,
  LiveRefundOwed,
  LiveShipmentAddress,
} from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { type FormStatus, idleForm, idleLoad, type LoadStatus } from "../../core/primitives/load-status";

export interface AddressDraft {
  name: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
}

/** A blank form, or the address already on the shipment so a typo is a tweak rather than a retype. */
export const addressDraft = (a: LiveShipmentAddress | null): AddressDraft => ({
  name: a?.name ?? "",
  street1: a?.street1 ?? "",
  street2: a?.street2 ?? "",
  city: a?.city ?? "",
  state: a?.state ?? "",
  zip: a?.zip ?? "",
  country: a?.country ?? "US",
  phone: a?.phone ?? "",
});

export type ShipmentMove = "label" | "shipped";

/** The open parcels' lenses. `posted` is what already left; everything else is work. */
export type ShippingFilter = "todo" | "needs-address" | "to-pack" | "posted" | "all";

export const SHIPPING_FILTERS: { key: ShippingFilter; label: string }[] = [
  { key: "todo", label: "To do" },
  { key: "needs-address", label: "Needs address" },
  { key: "to-pack", label: "To pack" },
  { key: "posted", label: "Posted" },
  { key: "all", label: "All" },
];

export function shipmentMatches(shipment: LiveOpenShipment, filter: ShippingFilter): boolean {
  const s = shipment.status;
  const posted = s === "SHIPPED" || s === "DELIVERED";
  switch (filter) {
    case "all":
      return true;
    case "posted":
      return posted;
    case "needs-address":
      return s === "AWAITING_ADDRESS";
    case "to-pack":
      return s === "AWAITING_PAYMENT" || s === "PAID" || s === "LABEL_CREATED";
    default:
      return !posted && s !== "CANCELLED";
  }
}

export class ShippingState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly shipments = new RxList<LiveOpenShipment>();
  readonly refundsOwed = new RxList<LiveRefundOwed>();
  readonly carrierShipping = new Rx(false);
  readonly shippingNote = new Rx<string | null>(null);
  readonly filter = new Rx<ShippingFilter>("todo");
  readonly csv = new Rx<FormStatus>(idleForm);
  /** The shipment whose address form is open. */
  readonly editing = new Rx<string | null>(null);
  readonly address = new Rx<AddressDraft>(addressDraft(null));
  /** The carrier's remarks on the last save: advisory, the address is saved either way. */
  readonly addressMessages = new RxList<string>();
  readonly addressSave = new Rx<FormStatus>(idleForm);
  readonly rowBusy = new Rx<{ shipmentId: string; move: ShipmentMove } | null>(null);
  readonly rowError = new Rx<{ shipmentId: string; message: string } | null>(null);
  /** The shipment whose label is waiting on the shop's phone, which the carrier needs on it. */
  readonly phonePrompt = new Rx<string | null>(null);
  readonly senderPhone = new Rx("");
  /** The shipment whose packing slip is open, fetched fresh: printing a stale one is how the wrong cards go out. */
  readonly slipShipmentId = new Rx<string | null>(null);
  readonly slipStatus = new Rx<LoadStatus>(idleLoad);
  readonly slip = new Rx<LivePackingSlip | null>(null);
}

export abstract class ShippingActions {
  abstract setFilter(filter: ShippingFilter): void;
  abstract reload(): Promise<void>;
  abstract exportCsv(): Promise<void>;
  abstract editAddress(shipment: LiveOpenShipment): void;
  /** Closes the form and re-reads the list, whether or not anything was saved. */
  abstract closeAddress(): void;
  abstract setAddressField(key: keyof AddressDraft, value: string): void;
  abstract saveAddress(): Promise<void>;
  /** Sends the phone typed into the prompt when that shipment's label asked for one. */
  abstract buyLabel(shipmentId: string): Promise<void>;
  abstract setSenderPhone(value: string): void;
  abstract markPosted(shipmentId: string): Promise<void>;
  abstract openSlip(shipmentId: string): void;
  abstract closeSlip(): void;
}
